import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, LogOut, Loader2, ShieldCheck, ShieldAlert, ArrowLeft } from "lucide-react";
import { AdminSubmissions } from "@/components/site/AdminSubmissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "btr-admin-token";

export default function AdminPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [constructionOn, setConstructionOn] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // On mount: if token exists, verify and fetch current setting
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const s = await axios.get(`${API}/settings/site`);
        if (!cancelled) setConstructionOn(Boolean(s.data.under_construction));
      } catch (_) {}
      if (token) {
        try {
          await axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } });
        } catch (_) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
        }
      }
      if (!cancelled) setBootLoading(false);
    };
    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { headers });
    } catch (_) {}
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setNotice("");
  };

  const handleToggle = async (next) => {
    if (loading) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const res = await axios.post(
        `${API}/admin/settings`,
        { under_construction: next },
        { headers }
      );
      setConstructionOn(Boolean(res.data.under_construction));
      setNotice(`Setting saved successfully.`);
      setTimeout(() => setNotice(""), 3500);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setError("Session expired. Please log in again.");
      } else {
        setError(err?.response?.data?.detail || "Could not update setting.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (bootLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-zinc-500 font-body text-sm">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Initializing admin…
      </div>
    );
  }

  // Login screen
  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505] text-white font-body flex items-center justify-center p-5">
        <div className="btr-gate-bg" />
        <form
          onSubmit={handleLogin}
          className="relative z-10 w-full max-w-md btr-glass rounded-2xl p-7 sm:p-9"
          data-testid="admin-login-form"
        >
          <div className="text-[11px] tracking-[0.3em] uppercase font-display text-[#a2020b] mb-4 flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5" />
            Private Admin
          </div>
          <h1 className="font-display font-extrabold text-white text-3xl tracking-tight">
            Site Control
          </h1>
          <p className="text-zinc-400 text-sm mt-2">
            Authorized access only. Enter your admin credentials to continue.
          </p>

          <label className="block font-display text-[11px] tracking-[0.2em] uppercase text-zinc-500 mt-7">
            Password
          </label>
          <div className="relative mt-2">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a2020b]" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              autoComplete="current-password"
              className="btr-input w-full bg-[#0a0a0a] border border-white/10 rounded-md py-3.5 pl-10 pr-4 text-white text-sm focus:border-[#a2020b] focus:outline-none transition-all"
              placeholder="••••••••••"
              data-testid="admin-password"
            />
          </div>

          {error && (
            <div className="mt-4 text-[#fca5a5] bg-[#a2020b]/10 border border-[#a2020b]/30 rounded-md px-3 py-2 text-xs font-body" data-testid="admin-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btr-btn-primary w-full mt-5"
            disabled={loading || !password}
            data-testid="admin-login-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Authenticating
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4" /> Log In
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-4 text-zinc-500 text-xs tracking-[0.2em] uppercase font-display hover:text-zinc-200 transition-colors inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-3 h-3" /> Back to site
          </button>
        </form>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-[#050505] text-white font-body" data-testid="admin-dashboard">
      <div className="absolute inset-0 btr-grid opacity-15 pointer-events-none" />
      <div className="relative max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <div className="flex items-center justify-between mb-10">
          <div className="text-[11px] tracking-[0.3em] uppercase font-display text-[#a2020b] flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            Admin · Site Control
          </div>
          <button
            onClick={handleLogout}
            className="text-zinc-400 hover:text-[#a2020b] text-xs tracking-[0.2em] uppercase font-display inline-flex items-center gap-1.5 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>

        <h1 className="font-display font-extrabold text-white text-4xl sm:text-5xl leading-[1.05] tracking-tight">
          Under Construction Mode
        </h1>
        <p className="text-zinc-400 text-base mt-4 leading-relaxed max-w-xl">
          When this mode is ON, visitors see the password-protected preview gate before reaching the live site.
          When OFF, visitors go directly to the loading screen and the full website.
        </p>

        <div className="btr-glass rounded-2xl p-6 sm:p-8 mt-10">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[11px] tracking-[0.3em] uppercase font-display text-zinc-500">
                Current Status
              </div>
              <div
                className={`font-display font-extrabold text-2xl sm:text-3xl mt-2 ${
                  constructionOn ? "text-[#a2020b]" : "text-emerald-400"
                }`}
                data-testid="status-text"
              >
                Under Construction Mode is currently{" "}
                <span className="uppercase">{constructionOn ? "ON" : "OFF"}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => handleToggle(!constructionOn)}
              disabled={loading || constructionOn === null}
              className={`relative inline-flex items-center w-20 h-10 rounded-full transition-colors border ${
                constructionOn
                  ? "bg-[#a2020b] border-[#a2020b]/80"
                  : "bg-emerald-500/80 border-emerald-400/80"
              } disabled:opacity-60`}
              aria-pressed={constructionOn ? "true" : "false"}
              data-testid="construction-toggle"
            >
              <span
                className={`absolute top-1 left-1 h-8 w-8 rounded-full bg-white shadow-lg transition-transform ${
                  constructionOn ? "translate-x-0" : "translate-x-10"
                }`}
              />
            </button>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <button
              onClick={() => handleToggle(true)}
              disabled={loading || constructionOn === true}
              className="btr-btn-ghost"
              data-testid="set-on"
            >
              Turn ON
            </button>
            <button
              onClick={() => handleToggle(false)}
              disabled={loading || constructionOn === false}
              className="btr-btn-primary"
              data-testid="set-off"
            >
              Turn OFF — Go Live
            </button>
          </div>

          {notice && (
            <div className="mt-5 text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 rounded-md px-3 py-2 text-sm" data-testid="admin-notice">
              {notice}
            </div>
          )}
          {error && (
            <div className="mt-5 text-[#fca5a5] bg-[#a2020b]/10 border border-[#a2020b]/30 rounded-md px-3 py-2 text-sm" data-testid="admin-error">
              {error}
            </div>
          )}
        </div>

        <AdminSubmissions headers={headers} />

        <button
          type="button"
          onClick={() => navigate("/")}
          className="mt-10 text-zinc-500 text-xs tracking-[0.2em] uppercase font-display hover:text-zinc-200 transition-colors inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3 h-3" /> Back to site
        </button>
      </div>
    </div>
  );
}
